import { useState, type ReactNode } from 'react';
import { resolveLogoUrl } from '@traceio-org/auth-ui';
import {
  SUBSCRIBABLE_PRODUCT_KEYS,
  type AdminEnterpriseProvisioning,
  type AdminOrganizationDetail,
  type AdminProductSubscription,
  type Auth0OrganizationLink,
  type SubscribableProductKey,
} from '@traceio-org/platform-contracts';
import { PageSection, RecordLayout, RecordStatus } from '@traceio-org/ui';
import { AdminProblem } from '../../components/AdminProblem';
import { LoadingBlock, SavedNotice } from '../../components/feedback';
import { ReasonField, SelectField, reasonError } from '../../components/fields';
import { DateText, DateTimeText, ScrollTable, StatusBadge } from '../../components/records';
import { WriteDialog, useDialogTarget } from '../../components/WriteDialog';
import { useAdmin } from '../../context';
import {
  DOMAIN_KIND_LABELS,
  SSO_PROVIDER_LABELS,
  formatCount,
  productName,
  type StatusPresentation,
} from '../../format';
import { useAdminRead } from '../../useAdminRead';
import { useOrganizationScope } from './scope';

function Facts({ label, children }: { label: string; children: ReactNode }) {
  return (
    <dl className="admin-facts" aria-label={label}>
      {children}
    </dl>
  );
}

function Fact({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div>
      <dt>{term}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Summary({ organization }: { organization: AdminOrganizationDetail }) {
  // Status and slug sit under the heading; this strip holds the rest of the record's identity.
  return (
    <dl className="admin-facts admin-facts--summary" aria-label="Overview">
      <Fact term="Members">{formatCount(organization.memberCount)}</Fact>
      <Fact term="Created">
        <DateText iso={organization.createdAt} />
      </Fact>
      <Fact term="Organization ID">
        <code className="admin-id">{organization.id}</code>
      </Fact>
    </dl>
  );
}

function Branding({
  organization,
  assetBaseUrl,
}: {
  organization: AdminOrganizationDetail;
  assetBaseUrl: string;
}) {
  const { displayName, logoUrl, accentColor } = organization.branding;
  // The contract allows https URLs and platform asset paths only; resolveLogoUrl checks the result again.
  const logo = logoUrl ? resolveLogoUrl(logoUrl, assetBaseUrl) : null;
  return (
    <PageSection title="Branding" description="What people see on this organization's sign-in pages.">
      <Facts label="Branding">
        <Fact term="Display name">{displayName}</Fact>
        <Fact term="Logo">
          {logo ? (
            <span className="admin-logo">
              <img src={logo} alt={`${displayName} logo`} referrerPolicy="no-referrer" decoding="async" />
            </span>
          ) : (
            <span className="admin-muted">No logo</span>
          )}
        </Fact>
        <Fact term="Accent color">
          {accentColor ? (
            <span className="admin-swatch">
              {/* The contract allows only #RRGGBB here, so the value is safe as a background. */}
              <span className="admin-swatch__chip" style={{ background: accentColor }} aria-hidden="true" />
              <code>{accentColor.toUpperCase()}</code>
            </span>
          ) : (
            <span className="admin-muted">Not set</span>
          )}
        </Fact>
      </Facts>
    </PageSection>
  );
}

type SubscriptionDialog = { kind: 'add' } | { kind: 'end'; subscription: AdminProductSubscription };

const LIVE_STATUSES = new Set(['trialing', 'active', 'past_due']);

/**
 * The organization's product subscriptions. A live subscription puts the product on its members' hub and lets
 * platform-api allow it; the member dialog (Members) offers the organization's products. Staff with billing
 * management add one (no billing plan: plan key "bootstrap", billing contracts are separate) or end one, with a
 * reason; platform-api decides who may (POST /v1/admin/organizations/:id/subscriptions).
 */
function Products({ organization }: { organization: AdminOrganizationDetail }) {
  const { api } = useAdmin();
  const { abilities, reloadOrganization } = useOrganizationScope();
  const organizationId = organization.id;
  const key = `subscriptions\u0000${organizationId}`;
  const read = useAdminRead(key, (signal) => api.listProductSubscriptions(organizationId, signal));
  const subscriptions = read.dataKey === key && !read.failure ? (read.data?.items ?? null) : null;
  const live = (subscriptions ?? []).filter((s) => LIVE_STATUSES.has(s.status));
  const available = SUBSCRIBABLE_PRODUCT_KEYS.filter((k) => !live.some((s) => s.productKey === k));

  const [notice, setNotice] = useState<string | null>(null);
  const dialog = useDialogTarget<SubscriptionDialog>();
  const [productKey, setProductKey] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<{ productKey?: string; reason?: string }>({});
  const open = (target: SubscriptionDialog) => {
    setProductKey('');
    setReason('');
    setErrors({});
    dialog.show(target);
  };
  const target = dialog.target;

  return (
    <PageSection
      title="Products"
      description="The products this organization's members can use. Ending one removes it from their hub."
      action={
        abilities.manageSubscriptions && subscriptions && available.length > 0 ? (
          <button type="button" className="admin-action" onClick={() => open({ kind: 'add' })}>
            Add a product
          </button>
        ) : undefined
      }
    >
      <SavedNotice message={notice} />
      {read.failure ? (
        <AdminProblem failure={read.failure} subject="the products" onRetry={read.retry} />
      ) : !subscriptions ? (
        <LoadingBlock label="Loading the products" />
      ) : subscriptions.length === 0 ? (
        <p className="admin-muted">This organization has no product subscriptions.</p>
      ) : (
        <ScrollTable
          caption="Products and subscription statuses"
          className="admin-table admin-table--compact"
          busy={read.loading}
        >
          <thead>
            <tr>
              <th scope="col">Product</th>
              <th scope="col">Status</th>
              <th scope="col">Since</th>
              {abilities.manageSubscriptions ? <th scope="col">Actions</th> : null}
            </tr>
          </thead>
          <tbody>
            {subscriptions.map((subscription) => (
              <tr key={subscription.id}>
                <th scope="row">{productName(subscription.productKey)}</th>
                <td>
                  <StatusBadge status={subscription.status} />
                </td>
                <td>
                  <DateText iso={subscription.endsAt ?? subscription.startsAt} />
                </td>
                {abilities.manageSubscriptions ? (
                  <td>
                    {LIVE_STATUSES.has(subscription.status) ? (
                      <button
                        type="button"
                        className="admin-action admin-action--danger"
                        onClick={() => open({ kind: 'end', subscription })}
                      >
                        End
                      </button>
                    ) : null}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </ScrollTable>
      )}
      <WriteDialog
        key={dialog.key}
        open={dialog.open}
        onClose={dialog.close}
        title={
          target?.kind === 'end'
            ? `End ${productName(target.subscription.productKey)}`
            : `Add a product to ${organization.displayName}`
        }
        description={
          target?.kind === 'end'
            ? `${organization.displayName} loses ${productName(target.subscription.productKey)} now: it leaves its members' hub. Their product access is kept if you add it again.`
            : 'An active subscription without a billing plan. Billing contracts are signed separately, in Billing.'
        }
        submitLabel={target?.kind === 'end' ? 'End the subscription' : 'Add the product'}
        onSubmit={async () => {
          const found: { productKey?: string; reason?: string } = {};
          if (target?.kind !== 'end' && !productKey) found.productKey = 'Choose a product.';
          const reasonProblem = reasonError(reason);
          if (reasonProblem) found.reason = reasonProblem;
          setErrors(found);
          if (Object.keys(found).length > 0) return 'invalid';
          if (target?.kind === 'end') {
            await api.endProductSubscription(organizationId, target.subscription.id, reason);
          } else {
            await api.addProductSubscription(organizationId, productKey as SubscribableProductKey, reason);
          }
          return undefined;
        }}
        onSaved={() => {
          setNotice(
            target?.kind === 'end'
              ? `${productName(target.subscription.productKey)} ended.`
              : `${productName(productKey)} added. Give members access to it in Members.`,
          );
          read.retry();
          reloadOrganization();
        }}
      >
        {target?.kind === 'end' ? null : (
          <SelectField
            label="Product"
            value={productKey}
            onChange={setProductKey}
            options={[
              { value: '', label: 'Choose a product' },
              ...available.map((k) => ({ value: k, label: productName(k) })),
            ]}
            error={errors.productKey ?? null}
            required
          />
        )}
        <ReasonField value={reason} onChange={setReason} error={errors.reason ?? null} />
      </WriteDialog>
    </PageSection>
  );
}

function Domains({ organization }: { organization: AdminOrganizationDetail }) {
  return (
    <PageSection
      title="Domains"
      description="Custom hosts for this organization's employee apps and customer portal. Released hosts are not listed."
    >
      {organization.domains.length === 0 ? (
        <p className="admin-muted">This organization has no custom domains.</p>
      ) : (
        <ScrollTable
          caption="Domains with verification, TLS and lifecycle status"
          className="admin-table admin-table--domains"
        >
          <thead>
            <tr>
              <th scope="col">Host</th>
              <th scope="col">Kind</th>
              <th scope="col">Product</th>
              <th scope="col">Verification</th>
              <th scope="col">TLS</th>
              <th scope="col">Lifecycle</th>
            </tr>
          </thead>
          <tbody>
            {organization.domains.map((domain) => (
              <tr key={domain.id}>
                <th scope="row">
                  <code>{domain.host}</code>
                </th>
                <td>{DOMAIN_KIND_LABELS[domain.kind] ?? domain.kind}</td>
                <td>{productName(domain.applicationKey)}</td>
                <td>
                  <StatusBadge status={domain.verificationStatus} />
                </td>
                <td>
                  <StatusBadge status={domain.tlsStatus} />
                </td>
                <td>
                  <StatusBadge status={domain.lifecycle} />
                </td>
              </tr>
            ))}
          </tbody>
        </ScrollTable>
      )}
    </PageSection>
  );
}

function SingleSignOn({ organization }: { organization: AdminOrganizationDetail }) {
  const policy = organization.ssoPolicy;
  return (
    <PageSection title="Single sign-on" description="The organization's own sign-in, when it has one.">
      {policy ? (
        <>
          <Facts label="Single sign-on policy">
            <Fact term="Enforced">{policy.enforced ? 'Yes' : 'No'}</Fact>
            <Fact term="Provider">{SSO_PROVIDER_LABELS[policy.provider] ?? policy.provider}</Fact>
            <Fact term="Connection">
              <code>{policy.connection}</code>
            </Fact>
          </Facts>
          <p className="admin-muted admin-note">
            {policy.enforced
              ? 'Members must sign in through this connection.'
              : 'Members can also sign in with other methods.'}
          </p>
        </>
      ) : (
        <p className="admin-muted">No single sign-on policy is configured for this organization.</p>
      )}
    </PageSection>
  );
}

/** What one link run did, in a sentence (the dialog's confirmation). */
export function describeAuth0Link(result: Auth0OrganizationLink): string {
  const first = result.created ? 'Auth0 organization created and linked.' : 'Auth0 organization synced.';
  const added = `${result.membersAdded} member${result.membersAdded === 1 ? '' : 's'} added`;
  const present = `${result.membersAlreadyPresent} already there`;
  const skipped =
    result.membersSkipped.length > 0
      ? `, ${result.membersSkipped.length} not added yet (they have not signed in to Trace; run it again after they do)`
      : '';
  return `${first} ${added}, ${present}${skipped}.`;
}

/**
 * The Auth0 organization the product applications sign in to (Brokers, Carriers, Agents, Enterprise require one).
 * Platform admins link it here, and run it again to add members who joined since; platform-api does the work
 * (POST /v1/admin/organizations/:id/auth0-organization) and stays the authority on who may.
 */
function ProductSignIn({ organization }: { organization: AdminOrganizationDetail }) {
  const { api } = useAdmin();
  const { abilities, reloadOrganization } = useOrganizationScope();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonProblem, setReasonProblem] = useState<string | null>(null);
  const [result, setResult] = useState<Auth0OrganizationLink | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // An older platform-api does not send the field: unknown, not unlinked.
  const ref = result?.auth0OrganizationRef ?? organization.auth0OrganizationRef;
  const linked = typeof ref === 'string';
  const action = linked ? 'Sync to Auth0' : 'Link to Auth0';
  return (
    <PageSection
      title="Product sign-in"
      description="The Auth0 organization members sign in to the products with. Without it only the hub works."
    >
      <Facts label="Auth0 organization">
        <Fact term="Auth0 organization">
          {linked ? (
            <code>{ref}</code>
          ) : (
            <span className="admin-muted">{ref === null ? 'Not linked' : 'Unknown'}</span>
          )}
        </Fact>
        {result ? (
          <>
            <Fact term="Connections">
              {result.connections.length > 0 ? (
                result.connections.join(', ')
              ) : (
                <span className="admin-muted">None</span>
              )}
            </Fact>
            <Fact term="Not added yet">
              {result.membersSkipped.length === 0 ? (
                <span className="admin-muted">Nobody</span>
              ) : (
                <ul className="admin-plain-list">
                  {result.membersSkipped.map((m) => (
                    <li key={m.userId}>
                      <code className="admin-id">{m.userId}</code> (never signed in)
                    </li>
                  ))}
                </ul>
              )}
            </Fact>
          </>
        ) : null}
      </Facts>
      <SavedNotice message={notice} />
      {abilities.linkAuth0Organization ? (
        <button
          type="button"
          className="admin-action"
          onClick={() => {
            setReason('');
            setReasonProblem(null);
            setOpen(true);
          }}
        >
          {action}
        </button>
      ) : null}
      <WriteDialog
        open={open}
        onClose={() => setOpen(false)}
        title={`${action}: ${organization.displayName}`}
        description="Creates or repairs its Auth0 organization and adds every member who has signed in to Trace."
        submitLabel={action}
        onSubmit={async () => {
          const problem = reason.trim() === '' ? null : reasonError(reason);
          setReasonProblem(problem);
          if (problem) return 'invalid';
          const answer = await api.linkAuth0Organization(organization.id, reason);
          setResult(answer);
          setNotice(describeAuth0Link(answer));
          return undefined;
        }}
        onSaved={reloadOrganization}
      >
        <p>
          Safe to run more than once. Members who never signed in to Trace have no Auth0 account yet: run it
          again after they do.
        </p>
        <ReasonField value={reason} onChange={setReason} error={reasonProblem} optional />
      </WriteDialog>
    </PageSection>
  );
}

const ENTERPRISE_STATES: Readonly<Record<AdminEnterpriseProvisioning['state'], StatusPresentation>> = {
  waiting_for_owner: { label: 'Waiting for the owner', tone: 'info' },
  provisioning: { label: 'In progress', tone: 'info' },
  needs_attention: { label: 'Needs attention', tone: 'danger' },
  complete: { label: 'Set up', tone: 'positive' },
};

const ENTERPRISE_EXPLANATIONS: Readonly<Record<AdminEnterpriseProvisioning['state'], string>> = {
  waiting_for_owner:
    'Trace Enterprise is set up with the owner as its administrator once they accept their invitation.',
  provisioning: 'Trace is setting it up. A failed attempt is tried again after a minute, then after five.',
  needs_attention: 'Three attempts in a row failed. Retry once the cause is fixed.',
  complete: 'The organization exists in Trace Enterprise.',
};

/**
 * Trace Enterprise for an organization created in Trace Admin with Enterprise (New organization): whether Enterprise
 * is set up yet, why not, and, for platform admins, a retry once it needs attention. Shown only when there is one
 * (GET /v1/admin/organizations/:id/enterprise-provisioning answers null otherwise).
 */
function EnterpriseSetup({ organization }: { organization: AdminOrganizationDetail }) {
  const { api } = useAdmin();
  const { abilities } = useOrganizationScope();
  const key = `enterprise-provisioning\u0000${organization.id}`;
  const read = useAdminRead(key, (signal) => api.getEnterpriseProvisioning(organization.id, signal));
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonProblem, setReasonProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  if (read.failure) {
    return (
      <PageSection title="Trace Enterprise setup">
        <AdminProblem failure={read.failure} subject="the Enterprise setup" onRetry={read.retry} />
      </PageSection>
    );
  }
  const provisioning = read.dataKey === key ? (read.data?.provisioning ?? null) : null;
  if (!provisioning) return null;
  const state = ENTERPRISE_STATES[provisioning.state];
  return (
    <PageSection title="Trace Enterprise setup" description={ENTERPRISE_EXPLANATIONS[provisioning.state]}>
      <Facts label="Trace Enterprise setup">
        <Fact term="State">
          <RecordStatus tone={state.tone}>{state.label}</RecordStatus>
        </Fact>
        {provisioning.lastError ? (
          <Fact term="Last failure">
            <code>{provisioning.lastError}</code>
          </Fact>
        ) : null}
        {provisioning.state === 'provisioning' && provisioning.nextAttemptAt ? (
          <Fact term="Next attempt">
            <DateTimeText iso={provisioning.nextAttemptAt} />
          </Fact>
        ) : null}
        {provisioning.enterpriseOrganizationRef ? (
          <Fact term="Enterprise organization">
            <code>{provisioning.enterpriseOrganizationRef}</code>
          </Fact>
        ) : null}
      </Facts>
      <SavedNotice message={notice} />
      {abilities.createOrganizations && provisioning.state === 'needs_attention' ? (
        <button
          type="button"
          className="admin-action"
          onClick={() => {
            setReason('');
            setReasonProblem(null);
            setOpen(true);
          }}
        >
          Retry
        </button>
      ) : null}
      <WriteDialog
        open={open}
        onClose={() => setOpen(false)}
        title="Retry the Enterprise setup"
        description={`Calls Trace Enterprise again now for ${organization.displayName}, with a fresh count of attempts.`}
        submitLabel="Retry now"
        onSubmit={async () => {
          const problem = reasonError(reason);
          setReasonProblem(problem);
          if (problem) return 'invalid';
          await api.retryEnterpriseProvisioning(organization.id, reason);
          return undefined;
        }}
        onSaved={() => {
          setNotice('Retrying the Enterprise setup now.');
          read.retry();
        }}
      >
        <ReasonField value={reason} onChange={setReason} error={reasonProblem} />
      </WriteDialog>
    </PageSection>
  );
}

/**
 * The overview of an organization (the milestone 1 detail): identity, branding, products and their
 * statuses, domains with verification, TLS and lifecycle, and the SSO policy, all from the organization
 * read the page heading already made.
 */
export function OverviewTab() {
  const { organization } = useOrganizationScope();
  const { assetBaseUrl } = useAdmin();
  return (
    <RecordLayout
      summary={<Summary organization={organization} />}
      aside={
        <>
          <Branding organization={organization} assetBaseUrl={assetBaseUrl} />
          <SingleSignOn organization={organization} />
          <ProductSignIn organization={organization} />
          <EnterpriseSetup organization={organization} />
        </>
      }
    >
      <Products organization={organization} />
      <Domains organization={organization} />
    </RecordLayout>
  );
}
